import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, X, MessageSquare, ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui'
import { useNewTaskNotification } from '@/hooks/useNewTaskNotification'
import { useUiStore } from '@/stores/uiStore'
import { useStreamChat } from '@/hooks'
import { cn, formatDate } from '@/lib/utils'

const AUTO_DISMISS_MS = 15000

export function NotificationBanner() {
  const { newTasks, dismissTask, dismissAll } = useNewTaskNotification()
  const navigate = useNavigate()
  const { setChatOpen } = useUiStore()
  const { send } = useStreamChat()
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Auto-dismiss after 15 seconds
  useEffect(() => {
    if (newTasks.length === 0) return

    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      dismissAll()
    }, AUTO_DISMISS_MS)

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [newTasks.length, dismissAll])

  if (newTasks.length === 0) return null

  const handleViewTask = () => {
    navigate('/tasks?status=pending')
    dismissAll()
  }

  const handleAskCopilot = (taskTitle: string) => {
    setChatOpen(true)
    send(`Tell me about the task: "${taskTitle}"`)
    dismissAll()
  }

  return (
    <div className="space-y-2 animate-in slide-in-from-top-2 duration-300">
      {newTasks.slice(0, 3).map((notification) => {
        const { task } = notification
        return (
          <div
            key={task.id}
            className={cn(
              'relative flex items-start gap-3 p-3 rounded-lg',
              'bg-primary/5 border border-primary/20',
              'animate-in slide-in-from-top-1 duration-200'
            )}
          >
            {/* Icon */}
            <div className="flex-shrink-0 mt-0.5">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Bell className="h-4 w-4 text-primary" />
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">New task assigned</p>
              <p className="text-sm text-foreground/90 truncate">
                {task.title}
              </p>
              <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground">
                {task.client_name && <span>{task.client_name}</span>}
                {task.due_date && (
                  <span>Due: {formatDate(task.due_date)}</span>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 mt-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs gap-1"
                  onClick={handleViewTask}
                >
                  View Task
                  <ArrowRight className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs gap-1"
                  onClick={() => handleAskCopilot(task.title)}
                >
                  <MessageSquare className="h-3 w-3" />
                  Ask Copilot
                </Button>
              </div>
            </div>

            {/* Dismiss */}
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 flex-shrink-0"
              onClick={() => dismissTask(task.id)}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        )
      })}

      {newTasks.length > 3 && (
        <p className="text-xs text-muted-foreground text-center">
          +{newTasks.length - 3} more new tasks
        </p>
      )}
    </div>
  )
}
